import { useEffect, useState, useCallback } from "react";
import { Building2, Loader2, FileDown, Database, TrendingUp, AlertTriangle, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/common/DataTable";
import { api, apiError, API } from "@/lib/api";
import { fdate, mxn } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// EBR INSTITUCIONAL alimentada con datos reales de 12 meses (43ª-IX Bis).
const SEM = { Alto: "bg-red-100 text-red-700", Medio: "bg-amber-100 text-amber-700", Bajo: "bg-emerald-100 text-emerald-700" };

export default function EbrInstitucional() {
  const { user } = useAuth();
  const puede = ["oficial_cumplimiento", "admin_entidad", "analista_pld"].includes(user?.role);
  const puedeGenerar = ["oficial_cumplimiento", "admin_entidad"].includes(user?.role);

  const [data, setData] = useState(null);
  const [hist, setHist] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!puede) return;
    try {
      const [p, h] = await Promise.all([
        api.get("/pld/ebr/preview").then((r) => r.data),
        api.get("/pld/ebr").then((r) => r.data).catch(() => ({ ebr: [] })),
      ]);
      setData(p); setHist(h.ebr || []);
    } catch (e) { console.debug("EBR institucional no disponible (sin acceso):", e?.response?.status); }
  }, [puede]);
  useEffect(() => { load(); }, [load]);

  const generar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pld/ebr/generar", { periodo: String(new Date().getFullYear()), meses: 12 });
      toast.success(`EBR ${data.ebr.periodo} generada y conservada (retención ${data.ebr.retencion_anios} años).`);
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const descargar = async (rec) => {
    try {
      const token = localStorage.getItem("cos_token");
      const res = await fetch(`${API}/pld/ebr/${rec.id}/evidencia`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("No se pudo descargar la evidencia.");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `EBR_${rec.periodo}.json`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(e.message); }
  };

  if (!puede || !data) return null;
  const inst = data.institucional;
  const dr = data.datos_reales;

  return (
    <div className="mb-6 rounded-lg border border-primary/20 bg-primary/5 p-4" data-testid="ebr-institucional">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-900">
          <Building2 className="h-4 w-4 text-primary" /> EBR institucional con datos reales (43ª-IX Bis) — últimos {dr.meses} meses
        </h3>
        {puedeGenerar && (
          <Button data-testid="ebr-generar-btn" size="sm" disabled={busy} onClick={generar} className="bg-primary hover:bg-sidebar-hover">
            {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Database className="mr-1.5 h-4 w-4" />} Generar y conservar EBR del periodo
          </Button>
        )}
      </div>

      {/* Comparativo real vs declarado */}
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="ebr-real">
          <p className="text-xs text-slate-500">Riesgo calculado con DATOS REALES</p>
          <div className="mt-1 flex items-center gap-2 font-heading text-2xl font-semibold">{inst.total_real} <Badge className={SEM[inst.grado_real]}>{inst.grado_real}</Badge></div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="ebr-declarado">
          <p className="text-xs text-slate-500">Riesgo DECLARADO (cuestionario/cédulas)</p>
          <div className="mt-1 flex items-center gap-2 font-heading text-2xl font-semibold">{inst.total_declarado} <Badge className={SEM[inst.grado_declarado]}>{inst.grado_declarado}</Badge></div>
        </div>
        <div className={`rounded-md border p-3 ${inst.alerta_desviacion ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`} data-testid="ebr-desviacion">
          <p className="text-xs text-slate-500">Desviación (real − declarado)</p>
          <p className="mt-1 flex items-center gap-2 font-heading text-2xl font-semibold">
            <TrendingUp className="h-5 w-5" /> {inst.desviacion_puntos > 0 ? "+" : ""}{inst.desviacion_puntos} pts
          </p>
          {inst.alerta_desviacion && <p className="mt-1 flex items-center gap-1 text-xs text-red-600"><AlertTriangle className="h-3 w-3" /> Desviación relevante: revisar.</p>}
        </div>
      </div>

      {/* Score por factor (datos reales) */}
      <DataTable testid="table-ebr-factores" rows={inst.factores}
        columns={[
          { key: "nombre", label: "Factor" },
          { key: "score_real", label: "Score real (datos)", render: (r) => `${r.score_real}` },
          { key: "ponderacion", label: "Ponderación", render: (r) => `${r.ponderacion}%` },
          { key: "eleva", label: "Eleva", render: (r) => r.eleva ? <Badge className="bg-red-100 text-red-700">Sí</Badge> : <Badge variant="outline">No</Badge> },
        ]} emptyLabel="Sin factores" />

      {/* Insumos reales agregados */}
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4" data-testid="ebr-insumos">
        <div><p className="text-xs text-slate-500">Clientes</p><p className="font-medium">{dr.clientes.total} · {dr.clientes.por_grado.Alto} alto · {dr.clientes.pep} PEP</p></div>
        <div><p className="text-xs text-slate-500">País GAFI (43ª-X-c)</p><p className="font-medium">{dr.clientes.pais_gafi ?? 0} cliente(s)</p></div>
        <div><p className="text-xs text-slate-500">Operaciones (12m)</p><p className="font-medium">{dr.operaciones.total} · {mxn(dr.operaciones.monto_total)}</p></div>
        <div><p className="text-xs text-slate-500">Efectivo / Relevantes</p><p className="font-medium">{dr.operaciones.efectivo_num} / {dr.operaciones.relevantes_num}</p></div>
      </div>

      {/* Histórico de EBR conservadas */}
      {hist.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-600"><History className="h-3.5 w-3.5" /> EBR conservadas (evidencia, 10 años)</h4>
          <DataTable testid="table-ebr-historico" rows={hist}
            columns={[
              { key: "periodo", label: "Periodo" },
              { key: "fecha_generacion", label: "Generada", render: (r) => fdate(r.fecha_generacion) },
              { key: "grado_real", label: "Grado real", render: (r) => <Badge className={SEM[r.grado_real]}>{r.grado_real} ({r.total_real})</Badge> },
              { key: "grado_declarado", label: "Declarado", render: (r) => `${r.grado_declarado} (${r.total_declarado})` },
              { key: "desviacion_puntos", label: "Desviación", render: (r) => `${r.desviacion_puntos > 0 ? "+" : ""}${r.desviacion_puntos}` },
            ]}
            actions={(row) => (
              <Button data-testid={`ebr-descargar-${row.id}`} size="sm" variant="ghost" onClick={() => descargar(row)}>
                <FileDown className="mr-1.5 h-4 w-4" /> Evidencia
              </Button>
            )}
            emptyLabel="Sin EBR conservadas" />
        </div>
      )}
    </div>
  );
}
